import type { TagTone } from "@/components/desktop/ui"
import type { CartagePayAccountState } from "@/schemas/cartagePayAccountState.schema"

export const ACCOUNT_STATE_TONES: Record<CartagePayAccountState, TagTone> = {
    not_started: "neutral",
    onboarding: "yellow",
    in_review: "blue",
    awaiting_consent: "blue",
    needs_info: "yellow",
    active: "green",
    paused: "red",
    rejected: "red",
    unknown: "neutral",
}

const BEFORE_APPROVAL: ReadonlySet<CartagePayAccountState> = new Set([
    "onboarding",
    "in_review",
    "awaiting_consent",
    "needs_info",
])

const SUBMITTED: ReadonlySet<CartagePayAccountState> = new Set([
    "in_review",
    "awaiting_consent",
    "active",
])

const WAITING_ON_THE_BANK: ReadonlySet<CartagePayAccountState> = new Set([
    "in_review",
    "awaiting_consent",
    "needs_info",
    "unknown",
])

const HELD_ACCOUNT_STATES = [
    "paused",
    "rejected",
    "awaiting_consent",
    "unknown",
] as const satisfies readonly CartagePayAccountState[]

export type HeldAccountState = (typeof HELD_ACCOUNT_STATES)[number]

export const isHeldAccountState = (
    state: CartagePayAccountState
): state is HeldAccountState =>
    (HELD_ACCOUNT_STATES as readonly CartagePayAccountState[]).includes(state)

export const isBeforeApproval = (state: CartagePayAccountState): boolean =>
    BEFORE_APPROVAL.has(state)

export const isApplicationSubmitted = (
    state: CartagePayAccountState | null
): boolean => state !== null && SUBMITTED.has(state)

export const shouldPollAccountState = ({
    accountState,
    isWalletProvisioned,
}: {
    accountState: CartagePayAccountState
    isWalletProvisioned: boolean
}): boolean =>
    WAITING_ON_THE_BANK.has(accountState) ||
    (accountState === "active" && !isWalletProvisioned)

const STATES_THAT_SHOW_RFIS: ReadonlySet<CartagePayAccountState> = new Set([
    "in_review",
    "needs_info",
    "active",
    "paused",
])

export const shouldReadRfis = (state: CartagePayAccountState): boolean =>
    STATES_THAT_SHOW_RFIS.has(state)

export const isWalletUsable = ({
    accountState,
    isWalletProvisioned,
}: {
    accountState: CartagePayAccountState
    isWalletProvisioned: boolean
}): boolean => accountState === "active" && isWalletProvisioned

export const shouldShowRfiScreen = ({
    isRfiOpen,
    accountState,
    isOrgAdmin,
    isEmbedConfigured,
}: {
    isRfiOpen: boolean
    accountState: CartagePayAccountState | null
    isOrgAdmin: boolean
    isEmbedConfigured: boolean
}): boolean =>
    isRfiOpen &&
    accountState !== null &&
    shouldReadRfis(accountState) &&
    isOrgAdmin &&
    isEmbedConfigured
